import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import {
  setAdminUserOrderRateLimit,
  type AdminOrderRateLimitResponse,
  type WireAdminOrderRateLimit,
  type WireAdminUserDetail,
} from '../../api/endpoints.js';
import { useApiMutation } from '../../api/hooks.js';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Field, Input, Modal } from '../../ui/index.js';
import { SoftBreaks } from './SoftBreaks.js';

/**
 * 2026-09-28 (operator request) — the account's order rate limit: how many
 * orders it may place per minute (the 429 rate_limited of POST
 * /api/v1/orders; website and API orders counted together) and the open-order
 * cap that follows from it (× 11, the 429 too_many_pending). The operator can
 * set this account's own limit — lower or higher than the default — (audited
 * PUT /admin/users/:id/order-rate-limit, capability user:adjust_balance) or
 * clear it. Every number comes from the server: `effective` and
 * `openOrderCap` are shown as sent, never recomputed here; the dialog's
 * bounds only mirror the server's checks, which stay authoritative.
 * Replaces the O4 "Açık sipariş limiti" card.
 */

/** Bounds the API accepts (ORDER_RATE_LIMIT_MIN / _MAX). */
const LIMIT_MIN = 1;
const LIMIT_MAX = 100_000;
/** The API keeps at most this many characters of the audit note. */
const NOTE_MAX = 500;

const fmt = (n: number): string => n.toLocaleString('tr-TR');
/** A limit of 0 means "no limit" on the server. */
const limitText = (n: number): string => (n > 0 ? fmt(n) : 'sınırsız');

const isCount = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;

interface KnownLimit {
  readonly override: number | null;
  readonly defaultLimit: number;
  readonly effective: number;
  readonly openOrderCap: number;
  /** The override is the limit in force. */
  readonly custom: boolean;
}

type LimitView =
  | { readonly kind: 'absent' }
  | { readonly kind: 'unknown'; readonly openOrders: number | null }
  | ({ readonly kind: 'known'; readonly openOrders: number | null } & KnownLimit);

function viewOf(raw: WireAdminOrderRateLimit | undefined): LimitView {
  if (!raw || typeof raw !== 'object') return { kind: 'absent' };
  const openOrders = isCount(raw.openOrders) ? raw.openOrders : null;
  if (
    raw.available !== true ||
    !Number.isInteger(raw.default) ||
    !isCount(raw.effective) ||
    !isCount(raw.openOrderCap)
  ) {
    return { kind: 'unknown', openOrders };
  }
  const override = isCount(raw.override) && raw.override > 0 ? raw.override : null;
  return {
    kind: 'known',
    openOrders,
    override,
    defaultLimit: raw.default,
    effective: raw.effective,
    openOrderCap: raw.openOrderCap,
    custom: override !== null && raw.effective === override,
  };
}

/** "Dakikalık sipariş limiti: …" — the limit in force and where it comes from. */
function LimitValue({ view }: { readonly view: LimitView }): JSX.Element {
  if (view.kind !== 'known') {
    return <>Dakikalık sipariş limiti: <strong>bilinmiyor</strong></>;
  }
  // Tabular digits for a number; "sınırsız" stays in the text face.
  const value = (
    <strong className={view.effective > 0 ? 'uinspect-mono' : undefined}>{limitText(view.effective)}</strong>
  );
  if (view.custom) {
    return <>Dakikalık sipariş limiti: {value} (özel; varsayılan {limitText(view.defaultLimit)})</>;
  }
  if (view.override !== null) {
    // A stored limit that is not in force (the default limit is switched off).
    return (
      <>
        Dakikalık sipariş limiti: {value} (varsayılan; kayıtlı özel limit {fmt(view.override)} etkisiz)
      </>
    );
  }
  return <>Dakikalık sipariş limiti: {value} (varsayılan)</>;
}

/** "Aynı anda en fazla X açık sipariş" — the derived open-order cap, plus the open orders now. */
function OpenOrdersLine({ view }: { readonly view: Exclude<LimitView, { kind: 'absent' }> }): JSX.Element {
  const now =
    view.openOrders !== null ? (
      <>
        {' '}· şu an <strong className="uinspect-mono">{fmt(view.openOrders)}</strong> açık
      </>
    ) : null;
  if (view.kind !== 'known') {
    return <span>Açık sipariş sınırı bilinmiyor{now}</span>;
  }
  if (view.openOrderCap <= 0) {
    return <span>Aynı anda açık sipariş sınırı yok{now}</span>;
  }
  return (
    <span>
      Aynı anda en fazla <strong className="uinspect-mono">{fmt(view.openOrderCap)}</strong> açık sipariş{now}
    </span>
  );
}

const RANGE_ERROR = `${fmt(LIMIT_MIN)} ile ${fmt(LIMIT_MAX)} arasında bir tam sayı girin.`;

/**
 * The new limit typed in the dialog: an integer 1..100.000; else null. The
 * card shows numbers the Turkish way (1.000), so a value typed the same way —
 * thousands grouped by a dot or a space — is read as that integer: "1.000" is
 * 1000, "100 000" is 100000. Anything else with a dot ("1.5", "10.00") is
 * refused, never guessed. The dialog echoes the parsed value before saving.
 */
function parseLimit(raw: string): number | null {
  let t = raw.trim();
  if (/^\d{1,3}([.\s\u00a0\u202f]\d{3})+$/.test(t)) t = t.replace(/[.\s\u00a0\u202f]/g, '');
  if (!/^\d{1,6}$/.test(t)) return null;
  const n = Number(t);
  return n >= LIMIT_MIN && n <= LIMIT_MAX ? n : null;
}

function limitErrorText(reason: string): ReactNode {
  switch (reason) {
    case 'limit_out_of_range':
      return RANGE_ERROR;
    case 'default_cap_disabled':
      return 'Varsayılan limit kapalı (sınırsız); özel limit gerekmez.';
    case 'forbidden':
    case 'capability_missing':
      return 'Bu işlem için yetkiniz yok.';
    case 'user_unknown':
      return 'Kullanıcı bulunamadı.';
    case 'limit_store_unavailable':
      return 'Limit kaydı şu an kullanılamıyor (migration 0080 uygulanmamış olabilir).';
    default:
      return (
        <>
          <code>limit</code>: <code><SoftBreaks text={reason} /></code>
        </>
      );
  }
}

const FORM_ID = 'user-order-rate-limit-form';

type Mode = 'edit' | 'clear';
type LimitBody = { readonly limit: number | null; readonly reason?: string };

export function OrderRateLimitCard({
  user,
  canAdjust,
  actorId,
  onChanged,
}: {
  readonly user: WireAdminUserDetail;
  readonly canAdjust: boolean;
  readonly actorId: string;
  readonly onChanged: () => void;
}): JSX.Element {
  // The numbers shown must belong to the account the actions target (the
  // route's `actorId`). While the page still shows another account's detail
  // (a stale read after moving from one account to another, or a failed read
  // of the new one), the card reads as unknown and nothing can be sent: a
  // PUT would otherwise change account B while the operator sees account A.
  const sameAccount = user.actorId === actorId;
  const view: LimitView = sameAccount ? viewOf(user.orderRateLimit) : { kind: 'unknown', openOrders: null };
  const known = view.kind === 'known' ? view : null;

  const [mode, setMode] = useState<Mode | null>(null);
  /**
   * The limit as it was when the dialog opened, and the account it was opened
   * for; the values are used while a refresh cannot read the limit.
   */
  const [snapshot, setSnapshot] = useState<{ readonly actorId: string; readonly limit: KnownLimit } | null>(null);
  /** The account the last successful save was for (its banner shows only there). */
  const [savedFor, setSavedFor] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [note, setNote] = useState('');
  const [dirty, setDirty] = useState(false);
  const mut = useApiMutation<LimitBody, AdminOrderRateLimitResponse>((opts, body) =>
    setAdminUserOrderRateLimit(opts, actorId, body),
  );
  const running = mut.state.kind === 'running';

  // Moving to another account closes a dialog that was opened for the previous
  // one and drops the previous result (its banner or error).
  const resetMutation = mut.reset;
  useEffect(() => {
    setMode(null);
    resetMutation();
  }, [actorId, resetMutation]);

  // Live values while the detail reads them, else the values the dialog opened
  // with — and only while the dialog belongs to the account on screen.
  const dialogSnapshot = mode !== null && sameAccount && snapshot?.actorId === actorId ? snapshot : null;
  const ctx: KnownLimit | null = dialogSnapshot ? (known ?? dialogSnapshot.limit) : null;
  const canEdit = canAdjust && sameAccount && known !== null && known.defaultLimit > 0;
  const canClear = canAdjust && sameAccount && known !== null;

  function open(next: Mode): void {
    if (!known || !sameAccount) return;
    mut.reset();
    setSnapshot({ actorId, limit: known });
    setDraft(next === 'edit' && known.override !== null ? String(known.override) : '');
    setNote('');
    setDirty(false);
    setMode(next);
  }
  function close(): void {
    if (running) return;
    setMode(null);
    mut.reset();
  }

  const parsed = mode === 'edit' ? parseLimit(draft) : null;
  const inputInvalid = mode === 'edit' && parsed === null;

  async function onConfirm(ev?: FormEvent): Promise<void> {
    ev?.preventDefault();
    // `ctx` is null unless the dialog was opened for the account on screen and
    // the card shows that account (sameAccount): never a PUT to another one.
    if (!mode || !ctx || running || !canAdjust || !sameAccount) return;
    setDirty(true);
    if (mode === 'edit' && parsed === null) return;
    const limit = mode === 'edit' ? parsed : null;
    const reason = note.trim().slice(0, NOTE_MAX);
    const target = actorId;
    const res = await mut.run(reason ? { limit, reason } : { limit });
    if (res.kind === 'ok') {
      setSavedFor(target);
      setMode(null);
      onChanged();
    } else if (res.kind === 'error' && (res.reason === 'default_cap_disabled' || res.reason === 'user_unknown')) {
      // The server's view differs from the page (default switched off,
      // account gone): refresh so the card shows the current state.
      onChanged();
    }
  }

  // Lowering the limit never cancels anything, but the open-order cap (limit
  // × 11, computed by the server) drops with it; say so before the operator
  // confirms. The open-order count is the live one, when the detail has it.
  const newLimit = mode === 'edit' ? parsed : ctx ? Math.max(ctx.defaultLimit, 0) : null;
  const lowers = ctx !== null && newLimit !== null && ctx.effective > 0 && newLimit > 0 && newLimit < ctx.effective;
  const openNow = known?.openOrders ?? null;

  // The success banner belongs to the account that was saved: never shown on
  // another account's card after the operator moves on.
  const saved =
    mut.state.kind === 'ok' && mode === null && sameAccount && savedFor === actorId
      ? mut.state.data?.data?.user?.orderRateLimit ?? null
      : null;
  const atCap =
    view.kind === 'known' && view.openOrderCap > 0 && view.openOrders !== null && view.openOrders >= view.openOrderCap;

  return (
    <Card flush data-testid="user-order-rate-limit-card">
      <CardHeader
        title="Dakikalık sipariş limiti"
        actions={
          known ? (
            known.custom ? (
              <Badge tone="orange" dot>Özel limit</Badge>
            ) : (
              <Badge tone="neutral">Varsayılan</Badge>
            )
          ) : undefined
        }
      />
      <CardBody>
        <div className="uinspect-toggle-row">
          <div className="uinspect-toggle-text">
            <div data-testid="user-order-rate-limit-value">
              <LimitValue view={view} />
            </div>
            {view.kind !== 'absent' ? (
              <div className="uinspect-inline-values" data-testid="user-order-rate-limit-open">
                <OpenOrdersLine view={view} />
                {atCap ? <Badge tone="warn" dot>açık sipariş sınırı dolu</Badge> : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="uinspect-note">
          {/* No break between a status code and its reason. */}
          Bir dakikada verilebilecek sipariş sayısı. Site ve API siparişleri (hesabın tüm API anahtarları) birlikte
          sayılır; limit dolunca yeni sipariş 429{' '}rate_limited ile reddedilir. Açık sipariş sınırı bu limitin 11
          katıdır (429{' '}too_many_pending).
        </div>
        <div className="uinspect-actions-row">
          <Button
            variant="secondary"
            size="sm"
            disabled={!canEdit}
            onClick={() => open('edit')}
            data-testid="user-order-rate-limit-edit"
          >
            Limiti değiştir
          </Button>
          {known && known.override !== null ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={!canClear}
              onClick={() => open('clear')}
              data-testid="user-order-rate-limit-clear"
            >
              Varsayılana dön
            </Button>
          ) : null}
        </div>
        {saved ? (
          <div className="uinspect-feedback">
            <Banner tone="success" testId="user-order-rate-limit-saved">
              {saved.override === null
                ? `Özel limit kaldırıldı; hesap varsayılan limitte (dakikada ${limitText(saved.effective)} sipariş).`
                : `Dakikalık sipariş limiti kaydedildi: ${limitText(saved.effective)}${
                    saved.openOrderCap > 0 ? ` (aynı anda en fazla ${fmt(saved.openOrderCap)} açık sipariş)` : ''
                  }.`}
            </Banner>
          </div>
        ) : null}
      </CardBody>
      <Modal
        open={mode !== null && ctx !== null}
        onClose={close}
        closeOnScrimClick={!running}
        closeOnEsc={!running}
        title={mode === 'clear' ? 'Varsayılana dön' : 'Dakikalık sipariş limitini değiştir'}
        width={480}
        testId="user-order-rate-limit-modal"
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={running}>
              Vazgeç
            </Button>
            {/* A submit button of the dialog's form (form attribute): Enter in
                either field submits, like a click. */}
            <Button
              variant="primary"
              type="submit"
              form={FORM_ID}
              disabled={!canAdjust || inputInvalid}
              loading={running}
              data-testid="user-order-rate-limit-confirm"
            >
              {mode === 'clear' ? 'Varsayılana dön' : 'Onayla ve kaydet'}
            </Button>
          </>
        }
      >
        {mode !== null && ctx ? (
          <form
            id={FORM_ID}
            className="uinspect-modal-body"
            onSubmit={onConfirm}
            noValidate
            aria-label={mode === 'clear' ? 'Özel limiti kaldır' : 'Dakikalık sipariş limitini değiştir'}
          >
            {mode === 'edit' ? (
              <Field
                label="Yeni limit (dakikada sipariş)"
                htmlFor="orlLimit"
                help={`${fmt(LIMIT_MIN)} ile ${fmt(LIMIT_MAX)} arasında bir tam sayı. Varsayılan ${limitText(ctx.defaultLimit)}; özel limit ondan düşük de olabilir, yüksek de.`}
                error={dirty && inputInvalid ? RANGE_ERROR : undefined}
              >
                {/* Text, not number: the value reaches parseLimit exactly as
                    typed ("1.000" included), whatever the browser locale. */}
                <Input
                  id="orlLimit"
                  type="text"
                  inputMode="numeric"
                  value={draft}
                  onChange={(e) => {
                    setDraft(e.target.value);
                    setDirty(true);
                  }}
                  onBlur={() => setDirty(true)}
                  invalid={dirty && inputInvalid}
                  autoComplete="off"
                  disabled={running}
                />
              </Field>
            ) : null}
            <p className="uinspect-modal-text" data-testid="user-order-rate-limit-summary">
              {mode === 'clear'
                ? `Özel limit kaldırılacak; hesap varsayılan limite (dakikada ${limitText(ctx.defaultLimit)} sipariş) döner. Değişiklik en geç 15 saniye içinde geçerli olur ve kayda geçer.`
                : parsed === null
                  ? `Şu anki limit: dakikada ${limitText(ctx.effective)} sipariş.`
                  : parsed === ctx.effective && ctx.custom
                    ? `Bu hesabın limiti zaten dakikada ${fmt(parsed)} sipariş. Kaydetmek değeri değiştirmez, yalnızca kayda geçer.`
                    : parsed === ctx.effective
                      ? `Bu hesap şu an da varsayılan limitte (dakikada ${fmt(parsed)} sipariş). Kaydetmek bu değeri hesaba özel limit olarak sabitler: varsayılan sonradan değişse de bu hesap ${fmt(parsed)} kalır.`
                      : `Bu hesabın limiti dakikada ${limitText(ctx.effective)} → ${fmt(parsed)} sipariş olacak. Değişiklik en geç 15 saniye içinde geçerli olur ve kayda geçer.`}
            </p>
            {lowers ? (
              <Banner tone="warn" testId="user-order-rate-limit-lower-warning">
                Limit düşürülürse açık siparişler iptal edilmez; yeni limiti aşan siparişler reddedilir. Açık sipariş
                sınırı da limitle birlikte düşer:{' '}
                {openNow !== null ? `hesabın şu an ${fmt(openNow)} açık siparişi var; bu sayı` : 'açık sipariş sayısı'} yeni
                sınıra ulaşmış ya da onu aşmışsa, sınırın altına inene kadar (genelde en fazla 10 dakika) yeni sipariş
                alınmaz.
              </Banner>
            ) : null}
            <Field label="Not (isteğe bağlı)" htmlFor="orlReason" help="Kayda geçer; en fazla 500 karakter.">
              <Input
                id="orlReason"
                type="text"
                value={note}
                maxLength={NOTE_MAX}
                onChange={(e) => setNote(e.target.value)}
                placeholder="örn. müşteri talebi #1234"
                autoComplete="off"
                disabled={running}
              />
            </Field>
            {mut.state.kind === 'error' || mut.state.kind === 'auth_required' ? (
              <div className="uinspect-feedback">
                <Banner tone="danger" testId="user-order-rate-limit-error">
                  {limitErrorText(mut.state.kind === 'error' ? mut.state.reason : 'auth_required')}
                </Banner>
              </div>
            ) : null}
          </form>
        ) : null}
      </Modal>
    </Card>
  );
}
